/**
 * Scene manager.
 * Uses the abstraction of a view - a display object container (BaseView) which has many components inside.
 */
import App from "../App";
import InstanceFactory from "../../src/factories/InstanceFactory";
import BaseView from "../views/BaseView";
import { Tween } from "./../Tween";
import Layers, { DefaultLayers } from "./Layers";
import ILayoutConfigSchema from "../baseConfigs/layoutConfigShema";
import ILayerMapConfig from "../baseConfigs/layerMapConfigSchema";
import Layer from "./Layer";
import DomLayer from "./DomLayer";
import { utils as PixiUtils, DisplayObject, Container } from "pixi.js";
import EventEmitter = PixiUtils.EventEmitter;
import ConsoleUtil from "./ConsoleUtil";

class ViewManager {

    public gameFactory;
    public subViews: Map<string, BaseView | DisplayObject | Array<BaseView | DisplayObject>>;
    public currentView: IViewSchema;
    protected dispatcher: EventEmitter;
    private layers: Layers;
    // used to save a "main" view to be recovered later for example recover reels after bonus game
    private savedView: IViewSchema;

    public init(layoutConfig: ILayoutConfigSchema, layerMapConfig: ILayerMapConfig, secondaryConfig: ILayoutConfigSchema) {
        this.gameFactory = InstanceFactory;
        this.subViews = new Map();
        this.layers = new Layers(App.instance.stage, layoutConfig, layerMapConfig, secondaryConfig);
    }

    public playDefault() {
        this.layers.playDefault();
    }

    public resetLayersZOrder() {
        this.layers.resetZOrder();
    }

    public get forceOrientationOnAllActive() {
       return this.layers.forceOrientationOnAllActive.bind(this.layers);
    }
    
    public moveLayerOnTop(leyerToMove: string, layerToMoveOnTopOf: string) {
        this.layers.moveLayerOnTop(leyerToMove, layerToMoveOnTopOf);
    }

    public moveLayerBehind(leyerToMove: string, layerToMoveBehindOf: string) {
        this.layers.moveLayerBehind(leyerToMove, layerToMoveBehindOf);
    }

    public getLayer(layer: string): Layer {
        return this.layers.getLayer(layer);
    }
    public hasLayer(layer: string): boolean {
        return this.layers?.hasLayer(layer);
    }

    public getDomLayer(layer: string): DomLayer {
        if ( layer === DefaultLayers.Background && this.layers.hasDomBackground()) {
            return this.layers.getDomBackground();
        }
    }

    public setView (viewType: string, saveCurrent: boolean = false): BaseView {
        let view;
        if (this.currentView && this.currentView.type == viewType) {
            return this.currentView.view;
        }
        if (this.savedView && this.savedView.type == viewType) {
            view = this.savedView.view;
            this.savedView = null;
        } else {
            if (saveCurrent) {
                this.saveCurrentView();
            }
            view = this.gameFactory.getClass(viewType);
        }
        if (this.currentView && this.currentView.view != view) {
            // TODO handle remove view logic
        }
        this.currentView = {type: viewType, view};

        return this.currentView.view;
    }

    public saveCurrentView() {
        this.savedView = this.currentView;
        return this.savedView;
    }

    public getSubview(subviewName: string, newInstance = false, destroyOld = false) {
        let subView = this.subViews.get(subviewName);
        if (subView && !newInstance) {
            return subView;
        } else if (subView && !destroyOld) {
            const temp = subView;
            const newSubview = this.gameFactory.getClass(subviewName);
            if (Array.isArray(temp)) {
                temp.push(newSubview);
            } else {
                subView = [temp, newSubview];
            }
            this.subViews.set(subviewName, subView);
            return newSubview;
        } else {
            if (subView && subView instanceof DisplayObject) {
                if (subView.parent) {
                    subView.parent.removeChild(subView);
                }

                subView.destroy();
            }
            const newSubview = this.gameFactory.getClass(subviewName);
            this.subViews.set(subviewName, newSubview);
            return newSubview;
        }
    }

    /**
     * Removes given subview from the displayList. If the subviewName given does not point to an array, index param is ignored.
     * @param viewName
     * @param index
     * @param destroy
     */
    public removeSubview(viewName: string, index = -1, destroy = true) {
        let subView = this.subViews.get(viewName);
        if (!subView) {
            throw new Error(`Subview ${viewName} cannot be removed because it doesn't exist`);
        }
        if (Array.isArray(subView)) {
            if (index != -1) {
                const viewToDelete = subView[index];
                Tween.killTweensOf(viewToDelete);
                subView.splice(index, 1);
                if (subView.length == 1) {
                    subView = subView[0];
                    this.subViews.set(viewName, subView);
                }
            } else {
                for (let i = 0; i < subView.length; i++) {
                    Tween.killTweensOf(subView[i]);
                    // TODO handle remove view logic
                }
                this.subViews.delete(viewName);
            }
        } else {
            Tween.killTweensOf(subView);
            // TODO handle remove view logic
            this.subViews.delete(viewName);
        }
    }

    public removeView(destroy: boolean = false) {
        const view = this.currentView.view;
        Tween.killTweensOf(view);
        this.currentView = null;
        // TODO handle remove view logic
    }

    private runDiag() {
        ConsoleUtil.log("Num children : " + this.getNumChildren(App.instance.stage));
    }

    private getNumChildren(parent: Container) {
        let numChildren = 1;
        if (parent.children) {
            for (let childIdx = 0; childIdx < parent.children.length; childIdx++) {
                if (parent.children[childIdx] instanceof Container) {
                    numChildren += this.getNumChildren(parent.children[childIdx] as Container);
                }
            }
        }
        return numChildren;
    }

}

export default new ViewManager();

export interface IViewSchema {
    type: string;
    view: BaseView;
}
